import { parseISO } from 'date-fns'
import { styled } from 'styled-components'

import type { ContractDto } from '@b-stock/contract-api-client'
import { useIsPast } from '@b-stock/fe-core/bstock-react'
import type { ListingDtoStatusEnum } from '@b-stock/listing-api-client'
import type { OfferingDto, OfferDto } from '@b-stock/offering-api-client'

import { hasUserWonAnOffering } from '@/buyer-portal/components/features/listing/ListingActionColumn/offering/hasUserWonAnOffering'
import { useIsSellerUserOfferingOwner } from '@/buyer-portal/components/features/listing/useIsSellerUserOfferingOwner'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'

import { CanceledOfferingBanner } from './banners/CanceledOfferingBanner'
import { ContractBannerInfo } from './banners/ContractBannerInfo'
import { ExpiredOfferingBanner } from './banners/ExpiredOfferingBanner'
import { OfferAcceptedBanner } from './banners/OfferAcceptedBanner'
import { OfferNotAcceptedBanner } from './banners/OfferNotAcceptedBanner'
import { PotentialNoPermissionsBanner } from './banners/PotentialNoPermissionsBanner'
import { useRefetchOffersOnDecisionEndTime } from './useRefetchOffersOnDecisionEndTime'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 1rem;

  &:empty {
    display: none;
  }
`

export const OfferingStatusBanner = ({
  offering,
  offers,
  contract,
  isListingIndexed,
  status,
}: {
  offering: OfferingDto
  offers: OfferDto[]
  contract: ContractDto | null
  isListingIndexed: boolean
  status: ListingDtoStatusEnum
}) => {
  const { decisionEndTime = '', offerEndTime = '' } = offering
  const isSellerUserOfferingOwner = useIsSellerUserOfferingOwner({
    offering,
  })
  const accountId = useAccountId()
  const isServiceAccountOnly = useIsServiceAccountOnly()

  const isCompleted = status === 'COMPLETED'
  const offerTimeEnded = useIsPast(parseISO(offerEndTime)) || isCompleted
  const decisionTimeEnded = useIsPast(parseISO(decisionEndTime)) || isCompleted

  const ignoreOffers = isSellerUserOfferingOwner || isServiceAccountOnly
  const isBidder = offers.length > 0 && !ignoreOffers

  const buyerWon = hasUserWonAnOffering({
    offering,
    offers,
    ignoreOffers,
    loggedInAccountId: accountId ?? '',
  })

  useRefetchOffersOnDecisionEndTime({
    offeringId: offering._id,
    listingId: offering.listingId,
    decisionTimeEnded,
    buyerWon,
  })

  if (status === 'CANCELED') return <CanceledOfferingBanner />

  return (
    <Container data-testid="offering-status-banner">
      {offerTimeEnded ? (
        buyerWon ? (
          <OfferAcceptedBanner offering={offering} contract={contract} />
        ) : isBidder && decisionTimeEnded ? (
          <OfferNotAcceptedBanner offering={offering} />
        ) : (
          <ExpiredOfferingBanner
            offering={offering}
            offers={offers}
            ignoreOffers={ignoreOffers}
          />
        )
      ) : (
        <>
          {contract ? <ContractBannerInfo /> : null}
          <PotentialNoPermissionsBanner isListingIndexed={isListingIndexed} />
        </>
      )}
    </Container>
  )
}
